<template>
  <div>
    <div ref="widget" />
    <p
      v-if="loading"
      role="status"
    >
      Beveiligingscontrole laden…
    </p>
    <p
      v-if="error"
      role="alert"
      class="text-red-700"
    >
      {{ error }}
    </p>
  </div>
</template>

<script>
import { defineComponent } from 'vue';

/** @type {Promise<string>|undefined} Shared Netlify site key and reCAPTCHA API loader. */
let recaptchaPromise;

/**
 * Read Netlify's injected configuration and load the explicit rendering API.
 *
 * @returns {Promise<string>} Netlify-provided public site key.
 */
async function initialiseRecaptcha() {
  try {
    /** @type {Response} Static form page response. */
    const response = await fetch('/__forms.html');
    if (!response.ok) {
      throw new Error('Unable to load Netlify forms');
    }

    /** @type {Document} Inert HTML document; its injected scripts are not executed. */
    const html = new DOMParser().parseFromString(await response.text(), 'text/html');
    /** @type {string|undefined} Public key supplied by Netlify at deployment. */
    const sitekey = html.querySelector('.g-recaptcha[data-sitekey]')?.dataset.sitekey;
    if (!sitekey) {
      throw new Error('Netlify reCAPTCHA is unavailable');
    }

    /**
     * Load Google's API without rendering the hidden widget.
     *
     * @param {function(): void} resolve API readiness callback.
     * @param {function(Error): void} reject Script failure callback.
     */
    const loadApi = (resolve, reject) => {
      /** Resolve when all reCAPTCHA dependencies are ready. */
      window.onClubeeRecaptchaLoad = () => {
        delete window.onClubeeRecaptchaLoad;
        resolve();
      };

      /** @type {HTMLScriptElement} Shared reCAPTCHA API script. */
      const script = document.createElement('script');
      script.src = 'https://www.google.com/recaptcha/api.js?onload=onClubeeRecaptchaLoad&render=explicit&hl=nl';
      script.async = true;
      script.defer = true;
      /** Remove the failed script so another page can retry loading it. */
      script.onerror = () => {
        delete window.onClubeeRecaptchaLoad;
        script.remove();
        reject(new Error('Unable to load reCAPTCHA'));
      };
      document.head.appendChild(script);
    };

    await new Promise(loadApi);
    return sitekey;
  } catch (error) {
    recaptchaPromise = undefined;
    throw error;
  }
}

/**
 * Load the Netlify-provided public key and Google's API once.
 *
 * @returns {Promise<string>} Public site key after API initialisation.
 */
function loadRecaptcha() {
  if (!recaptchaPromise) {
    recaptchaPromise = initialiseRecaptcha();
  }

  return recaptchaPromise;
}

export default defineComponent({
  /**
   * Initialise challenge state.
   *
   * @returns {Object} Challenge state.
   */
  data() {
    return {
      loading: true,
      error: '',
      widgetId: null,
      form: null,
    };
  },
  /**
   * Render a fresh challenge for this form, including after client-side navigation.
   *
   * @returns {Promise<void>} Completion of challenge initialisation.
   */
  async mounted() {
    this.form = this.$el.closest('form');
    this.form.addEventListener('submit', this.validate, true);

    try {
      /** @type {string} Netlify-provided public site key. */
      const sitekey = await loadRecaptcha();
      if (!this.$refs.widget) {
        return;
      }

      this.widgetId = window.grecaptcha.render(this.$refs.widget, {
        sitekey,
        size: 'compact',
        /** Clear feedback when the visitor completes the challenge. */
        callback: () => { this.error = ''; },
        /** Request a new challenge when the response expires. */
        'expired-callback': () => {
          this.error = 'De beveiligingscontrole is verlopen. Voer deze opnieuw uit.';
        },
        /** Explain a failed challenge. */
        'error-callback': () => {
          this.error = 'De beveiligingscontrole is mislukt. Probeer het later nog eens.';
        },
      });
    } catch (error) {
      this.error = 'De beveiligingscontrole kan niet worden geladen. Vernieuw de pagina en probeer opnieuw.';
    } finally {
      this.loading = false;
    }
  },
  /** Remove the form listener and clear the challenge before removing its DOM. */
  beforeUnmount() {
    this.form.removeEventListener('submit', this.validate, true);
    this.reset();
  },
  methods: {
    /**
     * Prevent submission until the challenge has been completed.
     *
     * @param {Event} event Form submission event.
     */
    validate(event) {
      if (this.widgetId === null || !window.grecaptcha.getResponse(this.widgetId)) {
        event.preventDefault();
        event.stopImmediatePropagation();
        if (!this.error) {
          this.error = 'Voer eerst de beveiligingscontrole uit.';
        }
      }
    },
    /** Reset the single-use token so failed submissions can be retried. */
    reset() {
      if (this.widgetId !== null) {
        window.grecaptcha.reset(this.widgetId);
      }
    },
  },
});
</script>
